import { Dialog } from "primereact/dialog";
import { ILogin } from "../../models/Login";
import { useEffect, useState } from "react";
import { baseUrl, Fetch } from "../../shared/Fetch";
import { Button } from "primereact/button";

interface Props {
  visible: boolean;
  taskId: number | null;
  toast: any;
  onHide: () => void;
}

interface HistoryItem {
  id: number;
  legalTaskId: number;
  action: string;
  changedBy: number;
  changedAt: string;
  old_Values: string;
  new_Values: string;
  current_File_Path: string | null;
}

const FIELD_LABELS: Record<string, string> = {
  Status: "Status",
  Assigned_To: "Assigned to",
  Assigned_By: "Assigned by",
  Task_Name: "Task name",
  Document_Type: "Document type",
  Document_Name: "Document name",
  Priority: "Priority",
  Assigne_Action_Status: "Accepted status",
  Assigne_Action_Reason: "Reason",
  Status_Support_Doc_Path: "Support doc",
  Status_Support_Reason: "Status reason",
  Created_By: "Created By",
};

const SKIP_FIELDS: Record<string, true> = {
  Id: true,
  PO_ID: true,
  // Created_By: true,
  Created_At: true,
  Updated_By: true,
  Updated_At: true,
  Assigned_At: true,
  Assigne_Action_At: true,
  Assigned_To_ID: true,
  Assigned_By_ID: true,
  Created_By_ID: true,
};

function parseJson(str: string): Record<string, any> {
  try {
    return JSON.parse(str);
  } catch {
    return {};
  }
}

function getDiff(oldObj: Record<string, any>, newObj: Record<string, any>) {
  const allKeys = Object.keys(oldObj).concat(Object.keys(newObj).filter((k) => !(k in oldObj)));
  const changes: { label: string; old: string; new: string }[] = [];

  for (const k of allKeys) {
    if (SKIP_FIELDS[k]) continue;
    const o = oldObj[k],
      n = newObj[k];
    if (o === n) continue;
    const oStr = o === null || o === undefined ? "—" : String(o);
    const nStr = n === null || n === undefined ? "—" : String(n);
    if (oStr === nStr || (oStr === "null" && nStr === "null")) continue;
    changes.push({
      label: FIELD_LABELS[k] ?? k.replace(/_/g, " "),
      old: oStr === "null" ? "—" : oStr,
      new: nStr === "null" ? "—" : nStr,
    });
  }
  return changes;
}

function getActionMeta(action: string) {
  const a = action.toLowerCase();
  if (a.includes("created")) return { icon: "pi-user-edit", color: "#185FA5", bg: "#E6F1FB", label: "Task Created" };
  if (a.includes("re-assigned")) return { icon: "pi-user-edit", color: "#185FA5", bg: "#E6F1FB", label: "Request Re-assigned" };
  if (a.includes("assigned")) return { icon: "pi-user-edit", color: "#185FA5", bg: "#E6F1FB", label: "Request Assigned" };
  if (a.includes("status")) return { icon: "pi-sync", color: "#3B6D11", bg: "#EAF3DE", label: "Status updated" };
  // if (a.includes("action")) return { icon: "pi-check-circle", color: "#0F6E56", bg: "#E1F5EE", label: " Request Accepted" };
  // if (a.includes("action taken")) return { icon: "pi-times-circle", color: "#A32D2D", bg: "#FCEBEB", label: "Request Rejected" };
  return { icon: "pi-pencil", color: "#5F5E5A", bg: "#F1EFE8", label: action };
}

function formatDt(str: string) {
  if (!str) return "";
  return new Date(str).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renderValue(val: string) {
  if (val === "true") return <span style={{ color: "#0F6E56", fontWeight: 500 }}>✔ Accepted</span>;
  if (val === "false") return <span style={{ color: "#A32D2D", fontWeight: 500 }}>✘ Rejected</span>;
  return <span>{val}</span>;
}

const LegalTaskHistory = ({ visible, onHide, toast, taskId }: Props) => {
  const [historyList, setHistoryList] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const response = await Fetch("Legal_Task/history", "GET", null, `taskId=${taskId}`, toast);
      if (response?.ok) {
        const data = await response.json();
        setHistoryList(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadDoc = async (taskHitoryID: number) => {
    const params = {
      method: "GET",
      headers: {
        "Content-Type": "application/msword",
        Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("auth") + "")?.token}`,
      },
    };
    try {
      const response = await fetch(`${baseUrl}${"Legal_Task/download"}?taskHitoryID=${taskHitoryID}`, params);
      if (response.status === 200) {
        const blob = await response.blob();

        const disposition = response.headers.get("content-disposition");
        let fileName = `Supportive_Document`;

        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) {
            fileName = match[1];
          }
        }

        const href = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(href);
      } else if (response.status === 400) {
        const message = await response.text();
        toast.current.show({ severity: "error", detail: message, life: 3000 });
      }
    } catch {
      toast.current.show({ severity: "error", detail: "Something went Wrong", life: 3000 });
    }
  };

  const download = (rowData: HistoryItem) => {
    return rowData.current_File_Path ? (
      <Button
        icon="pi pi-download"
        label="Download Document"
        className="p-button-rounded p-button-success p-button-text mr-1"
        onClick={() => {
          downloadDoc(rowData.id);
        }}
      />
    ) : (
      <></>
    );
  };

  useEffect(() => {
    if (visible && taskId) loadHistory();
    if (!visible) setHistoryList([]);
  }, [visible, taskId]);

  return (
    <Dialog visible={visible} header="Task History" style={{ width: "55vw" }} modal onHide={onHide}>
      {loading && (
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-color-secondary)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "1.5rem" }} />
        </div>
      )}

      {!loading && !historyList.length && <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-color-secondary)" }}>No history found.</div>}

      {!loading &&
        historyList.map((item, idx) => {
          const old = parseJson(item.old_Values);
          const nw = parseJson(item.new_Values);
          const changes = getDiff(old, nw);
          let meta = getActionMeta(item.action);
          const isLast = idx === historyList.length - 1;
          const reason = nw.Assigne_Action_Reason && nw.Assigne_Action_Reason !== "null" ? nw.Assigne_Action_Reason : null;
          const action = nw.Assigne_Action_Status || null;

          if (item.action === "Action Taken By Assigne") {
            const actionStatus = nw.Assigne_Action_Status;

            meta =
              actionStatus === true
                ? {
                    icon: "pi-check-circle",
                    color: "#0F6E56",
                    bg: "#E1F5EE",
                    label: "Request Accepted",
                  }
                : {
                    icon: "pi-times-circle",
                    color: "#A32D2D",
                    bg: "#FCEBEB",
                    label: "Request Rejected",
                  };
          }

          return (
            <div key={item.id} style={{ display: "flex", gap: "16px" }}>
              {/* Spine */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "32px", flexShrink: 0 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: meta.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <i className={`pi ${meta.icon}`} style={{ color: meta.color, fontSize: "14px" }} />
                </div>
                {!isLast && <div style={{ width: 2, flex: 1, minHeight: 20, background: "var(--surface-border)", margin: "2px 0" }} />}
              </div>

              {/* Body */}
              <div style={{ flex: 1, paddingBottom: isLast ? 0 : "24px" }}>
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      background: meta.bg,
                      color: meta.color,
                      fontSize: 11,
                      fontWeight: 500,
                      padding: "2px 10px",
                      borderRadius: 6,
                      display: "inline-block",
                    }}
                  >
                    {meta.label}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-color-secondary)" }}>{formatDt(item.changedAt)}</span>
                </div>

                {/* Card */}
                <div
                  style={{
                    background: "var(--surface-card)",
                    border: "1px solid var(--surface-border)",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ fontSize: 12, color: "var(--text-color-secondary)", marginBottom: 8 }}>
                    <i className="pi pi-user" style={{ marginRight: 4 }} />
                    Changed by{" "}
                    <span
                      style={{
                        color: "var(--primary-color)",
                        fontWeight: 600,
                      }}
                    >
                      {item.changedBy}
                    </span>
                  </div>

                  {changes.length === 0 && <div style={{ fontSize: 12, color: "var(--text-color-secondary)" }}>No field changes recorded</div>}

                  {changes.map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, margin: "4px 0" }}>
                      <span style={{ color: "var(--text-color-secondary)", minWidth: 120, fontSize: 12 }}>{c.label}</span>
                      {c.old !== "—" && (
                        <>
                          <span style={{ color: "var(--text-color-secondary)" }}>{c.label === "Support doc" ? "Old File" : c.old}</span>
                          <span style={{ color: "var(--text-color-secondary)", fontSize: 11 }}>→</span>
                        </>
                      )}
                      {c.label === "Support doc" ? (
                        download(item)
                      ) : (
                        <span
                          style={{
                            color: "var(--text-color)",
                            background: "var(--surface-200)",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: 13,
                            fontWeight: 600,
                            letterSpacing: "0.01em",
                          }}
                        >
                          {renderValue(c.new)}
                        </span>
                      )}
                    </div>
                  ))}

                  {/* {reason && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 10px",
                        background: "#FCEBEB",
                        borderRadius: 6,
                        fontSize: 12,
                        color: action === true ? "#2da335" : "#A32D2D",
                      }}
                    >
                      <i className="pi pi-send" style={{ marginRight: 4 }} />
                      {reason}
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          );
        })}
    </Dialog>
  );
};

export default LegalTaskHistory;
